import {z} from 'zod'

import {apiFetch} from '../api/fetch'
import {toApiDateString} from '../jobs/job-schedule-format'
import {
    type CreateProjectBillableLineItemInput,
    createProjectBillableLineItemRequestSchema,
    type CreateProjectBillingTermsInput,
    createProjectBillingTermsRequestSchema,
    type CreateProjectInput,
    type CreateProjectJobInput,
    createProjectJobRequestSchema,
    createProjectRequestSchema,
    type PatchProjectInput,
    patchProjectRequestSchema,
    type ProjectBillableLineItem,
    projectBillableLineItemSchema,
    type ProjectBillingTerms,
    projectBillingTermsSchema,
    type ProjectDetail,
    projectDetailSchema,
    type ProjectJobResponse,
    projectJobResponseSchema,
    type ProjectListFilters,
    type ProjectListItem,
    projectListItemSchema,
} from './schemas'

function dateFilterToApiParam(value: ProjectListFilters['createdAfter']): string | undefined {
    if (value == null) {
        return undefined
    }
    if (typeof value === 'string') {
        return value
    }
    return toApiDateString(value)
}

function toProjectListQueryParams(filters: ProjectListFilters = {}): URLSearchParams {
    const params = new URLSearchParams()

    const name = filters.name?.trim()
    if (name) {
        params.set('name', name)
    }

    if (filters.customerName?.trim()) {
        params.set('customer_name', filters.customerName.trim())
    }

    if (filters.customerId) {
        params.set('customer_id', filters.customerId)
    }

    const description = filters.description?.trim()
    if (description) {
        params.set('description', description)
    }

    const createdAfter = dateFilterToApiParam(filters.createdAfter)
    if (createdAfter) {
        params.set('created_after', createdAfter)
    }

    const createdBefore = dateFilterToApiParam(filters.createdBefore)
    if (createdBefore) {
        params.set('created_before', createdBefore)
    }

    const startDateFrom = dateFilterToApiParam(filters.startDateFrom)
    if (startDateFrom) {
        params.set('start_date_from', startDateFrom)
    }

    const startDateTo = dateFilterToApiParam(filters.startDateTo)
    if (startDateTo) {
        params.set('start_date_to', startDateTo)
    }

    if (filters.sortBy) {
        params.set('sort_by', filters.sortBy)
    }

    if (filters.sortDir) {
        params.set('sort_dir', filters.sortDir)
    }

    if (filters.status?.length) {
        for (const status of filters.status) {
            params.append('status', status)
        }
    }

    if (filters.hasUnbilled) {
        params.set('has_unbilled', 'true')
    }

    if (filters.mine) {
        params.set('mine', 'true')
    }

    return params
}

export async function listProjects(filters: ProjectListFilters = {}): Promise<ProjectListItem[]> {
    const params = toProjectListQueryParams(filters)
    const query = params.toString()
    const path = query ? `/projects?${query}` : '/projects'
    const raw = await apiFetch<unknown>(path)
    return z.array(projectListItemSchema).parse(raw)
}

export async function getProject(id: string): Promise<ProjectDetail> {
    const raw = await apiFetch<unknown>(`/projects/${id}`)
    return projectDetailSchema.parse(raw)
}

export async function createProject(input: CreateProjectInput): Promise<ProjectDetail> {
    const body = createProjectRequestSchema.parse(input)
    const raw = await apiFetch<unknown>('/projects', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return projectDetailSchema.parse(raw)
}

export async function patchProject(id: string, input: PatchProjectInput): Promise<ProjectDetail> {
    const body = patchProjectRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    })
    return projectDetailSchema.parse(raw)
}

export async function approveProject(id: string): Promise<ProjectDetail> {
    const raw = await apiFetch<unknown>(`/projects/${id}/approve`, {method: 'POST'})
    return projectDetailSchema.parse(raw)
}

/** Archive a project. The API returns 409 for planned jobs unless `confirmCancelPlanned` is set. */
export async function archiveProject(
    id: string,
    options: {confirmCancelPlanned?: boolean} = {},
): Promise<ProjectDetail> {
    const confirm = options.confirmCancelPlanned === true
    const raw = await apiFetch<unknown>(`/projects/${id}/archive`, {
        method: 'POST',
        ...(confirm ? {body: JSON.stringify({confirm_cancel_planned: true})} : {}),
    })
    return projectDetailSchema.parse(raw)
}

export async function reopenProject(id: string): Promise<ProjectDetail> {
    const raw = await apiFetch<unknown>(`/projects/${id}/unarchive`, {method: 'POST'})
    return projectDetailSchema.parse(raw)
}

export async function listProjectBillableLineItems(
    projectId: string,
): Promise<ProjectBillableLineItem[]> {
    const raw = await apiFetch<unknown>(`/projects/${projectId}/billable-line-items`)
    return z.array(projectBillableLineItemSchema).parse(raw)
}

export async function listProjectBillingTerms(projectId: string): Promise<ProjectBillingTerms[]> {
    const raw = await apiFetch<unknown>(`/projects/${projectId}/billing-terms`)
    return z.array(projectBillingTermsSchema).parse(raw)
}

export async function createProjectJob(
    projectId: string,
    input: CreateProjectJobInput,
): Promise<ProjectJobResponse> {
    const body = createProjectJobRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/projects/${projectId}/jobs`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return projectJobResponseSchema.parse(raw)
}

/** Create project jobs in a single transaction, idempotent on `requestId`. */
export async function createProjectJobsBatch(
    projectId: string,
    inputs: CreateProjectJobInput[],
    requestId: string,
): Promise<ProjectJobResponse[]> {
    const raw = await apiFetch<unknown>(`/projects/${projectId}/jobs/batch`, {
        method: 'POST',
        body: JSON.stringify({
            jobs: inputs.map((input) => createProjectJobRequestSchema.parse(input)),
            request_id: requestId,
        }),
    })
    return z.array(projectJobResponseSchema).parse(raw)
}

export async function createProjectBillingTerms(
    projectId: string,
    input: CreateProjectBillingTermsInput,
): Promise<ProjectBillingTerms> {
    const body = createProjectBillingTermsRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/projects/${projectId}/billing-terms`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return projectBillingTermsSchema.parse(raw)
}

export async function createProjectBillableLineItem(
    projectId: string,
    input: CreateProjectBillableLineItemInput,
): Promise<ProjectBillableLineItem[]> {
    const body = createProjectBillableLineItemRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/projects/${projectId}/billable-line-items`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return z.array(projectBillableLineItemSchema).parse(raw)
}

export async function updateProjectBillableLineItem(
    projectId: string,
    lineItemId: string,
    input: CreateProjectBillableLineItemInput,
): Promise<ProjectBillableLineItem[]> {
    const body = createProjectBillableLineItemRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(
        `/projects/${projectId}/billable-line-items/${lineItemId}`,
        {
            method: 'PUT',
            body: JSON.stringify(body),
        },
    )
    return z.array(projectBillableLineItemSchema).parse(raw)
}

export async function archiveProjectBillableLineItem(
    projectId: string,
    lineItemId: string,
): Promise<ProjectBillableLineItem[]> {
    const raw = await apiFetch<unknown>(
        `/projects/${projectId}/billable-line-items/${lineItemId}/archive`,
        {
            method: 'POST',
        },
    )
    return z.array(projectBillableLineItemSchema).parse(raw)
}
